<template>
  <v-flex xs12>
    <v-dialog
      v-model="exceptionsDialog.show"
      max-width="800"
      class="ma-2"
    >
      <ExceptionForm
        v-if="exceptionsDialog.show"
        :hashtag-id="exceptionsDialog.hashtagId"
        :hashtag-name="exceptionsDialog.hashtagName"
        :geotags="geotags"
        @error="showErrorMessage"
        @added="exceptionsAdded"
        @deleted="exceptionsDeleted"
      />
    </v-dialog>
    <v-data-table
      :options.sync="options"
      :items-per-page="10"
      :headers="headers"
      :items="hashtags"
      :loading="loading"
      loading-text="Loading... Please wait"
      :single-expand="true"
    >
      <template v-slot:top>
        <v-toolbar
          flat
        >
          <v-spacer></v-spacer>
          <tag-form v-on:create="addTag" />
        </v-toolbar>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="!hasSomeAbilities($constants.ABILITY.INTERN)"
          icon @click="remove(item.id)"
        >
          <v-icon
            color="red"
          >
            mdi-delete
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:item.name="{ item }">
        <v-edit-dialog
          @save="save(itemClone, item)"
          @open="itemClone = {...item}"
        >
          <v-chip>#{{ item.name }}</v-chip>
          <template v-slot:input>
            <v-text-field
              v-model="itemClone.name"
              label="Edit"
              single-line
              counter
            />
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:item.hashtag_exception_count="{ item }">
        <v-chip
          outlined
          :color="item.hashtag_exception_count ? '#8028BDFF' : 'gray'"
          :text-color="item.hashtag_exception_count ? '#8028BDFF' : 'gray'"
          @click="callExceptionsDialog(item.id, item.name)"
        >exc ({{ item.hashtag_exception_count }})</v-chip>
      </template>

      <template v-slot:item.title="{ item }">
        <v-edit-dialog
          @save="save(itemClone, item)"
          @open="itemClone = {...item}"
        >
          <div v-if="item.title" class="font-weight-black">{{ item.title }}</div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
          <template v-slot:input>
            <v-text-field
              v-model="itemClone.title"
              label="Edit"
              single-line
              counter
            />
          </template>
        </v-edit-dialog>

        <translations-bar
          :id="item.id"
          :translations="translationsTitle[item.id] ? translationsTitle[item.id] : {}"
          :model="$constants.TRANSLATION_MODELS.HASHTAG_TITLE"
          :title="item.title"
          :trigger-update="item.title"
        >
        </translations-bar>
      </template>

      <template v-slot:item.subtitle="{ item }">
        <v-edit-dialog
          @save="save(itemClone, item)"
          @open="itemClone = {...item}"
        >
          <div v-if="item.subtitle" class="font-weight-medium">{{ item.subtitle }}</div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
          <template v-slot:input>
            <v-text-field
              v-model="itemClone.subtitle"
              label="Edit"
              single-line
              counter
            />
          </template>
        </v-edit-dialog>

        <translations-bar
          :id="item.id"
          :translations="translationsSubtitle[item.id] ? translationsSubtitle[item.id] : {}"
          :model="$constants.TRANSLATION_MODELS.HASHTAG_SUBTITLE"
          :title="item.subtitle"
          :trigger-update="item.subtitle"
        >
        </translations-bar>
      </template>

      <template v-slot:item.enabled="{ item }">
        <v-switch
          v-model="item.enabled"
          @change="toggleDisabled(item)"
        />
      </template>
    </v-data-table>
  </v-flex>
</template>

<script>
import { mapActions } from 'vuex';
import TagForm from '../components/dialogs/TagForm.vue';
import ExceptionForm from '../components/ExceptionForm.vue';
import TranslationsBar from '../components/Translations/TranslationsBar.vue';

export default {
  name: 'Hashtag',
  components: {
    TranslationsBar,
    TagForm,
    ExceptionForm,
  },
  data: () => ({
    itemClone: {},
    hashtags: [],
    geotags: [],
    isLoadingGeotags: false,
    loading: true,
    exceptionsDialog: {
      hashtagId: NaN,
      hashtagName: '',
      show: false,
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['name'],
      sortDesc: [false],
      mustSort: true,
      multiSort: false,
    },
    headers: [
      { text: 'id', value: 'id' },
      { text: 'hashtag', value: 'name' },
      { text: 'exceptions', value: 'hashtag_exception_count' },
      { text: 'subtitle', value: 'subtitle' },
      { text: 'title', value: 'title' },
      { text: 'poi count', value: 'pois_count' },
      { text: 'enabled', value: 'enabled', sortable: false },
      { text: '', value: 'actions', sortable: false },
    ],
    translationsSubtitle: {},
    translationsTitle: {},
  }),

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    addTag() {
      this.getTags();
    },

    toggleDisabled(item) {
      this.$requestHelpers.patch(`api/admin/hashtags/${item.id}`, item);
    },

    /*
    eslint no-param-reassign:
    ["error", { "props": true, "ignorePropertyModificationsFor": ["item"] }]
    */
    save(itemClone, item) {
      this.$requestHelpers
        .patch(`api/admin/hashtags/${item.id}`, itemClone)
        .then(() => {
          this.setSnack({
            color: 'green',
            text: 'saved',
          });

          item.name = itemClone.name;
          item.title = itemClone.title;
          item.subtitle = itemClone.subtitle;
        });
    },

    remove(id) {
      this.$confirm('Are you sure you want to delete this hashtag?').then((res) => {
        if (!res) return;

        this.$requestHelpers
          .delete(`api/admin/hashtags/${id}`)
          .then(() => {
            this.hashtags = this.hashtags.filter((e) => e.id !== id);
          });
      });
    },

    getTags() {
      this.$requestHelpers
        .get('api/admin/hashtags',
          { params: { fulldata: true } })
        .then((response) => {
          this.hashtags = response.data;
          this.loading = false;
          this.getTranslationsSubtitle();
          this.getTranslationsTitle();
        });
    },

    getGeotagsList() {
      // skip if the list is already received
      if (this.geotags.length > 0) return;

      this.isLoadingGeotags = true;
      this.$requestHelpers.post(
        'api/admin/geotag/geotagsList', {}, {
          withOverlay: false,
        },
      ).then((response) => {
        this.geotags = response.data;
      }).finally(() => { this.isLoadingGeotags = false; });
    },

    getTranslationsSubtitle() {
      this.$requestHelpers.post(
        'api/admin/bulk-translations',
        {
          model: this.$constants.TRANSLATION_MODELS.HASHTAG_SUBTITLE,
          ids: this.hashtags.map((item) => item.id),
        },
      ).then((response) => {
        this.translationsSubtitle = response.data.result;
      });
    },

    getTranslationsTitle() {
      this.$requestHelpers.post(
        'api/admin/bulk-translations',
        {
          model: this.$constants.TRANSLATION_MODELS.HASHTAG_TITLE,
          ids: this.hashtags.map((item) => item.id),
        },
      ).then((response) => {
        this.translationsTitle = response.data.result;
      });
    },

    callExceptionsDialog(hashtagId, hashtagName) {
      this.getGeotagsList();
      this.exceptionsDialog.hashtagId = hashtagId;
      this.exceptionsDialog.hashtagName = hashtagName;
      this.exceptionsDialog.show = true;
    },

    showErrorMessage(message) {
      this.setSnack({
        color: 'red',
        text: message,
      });
    },

    showMessage(message = '') {
      this.setSnack({
        color: 'green',
        text: message || 'Success',
      });
    },

    exceptionsDeleted(hashtagId, newAmount) {
      const hashtagIndex = this.hashtags.findIndex((el) => el.id === hashtagId);
      this.hashtags[hashtagIndex].hashtag_exception_count = newAmount;
      this.showMessage('Exception was deleted');
    },

    exceptionsAdded(hashtagId, newAmount) {
      const hashtagIndex = this.hashtags.findIndex((el) => el.id === hashtagId);
      this.hashtags[hashtagIndex].hashtag_exception_count = newAmount;
      this.showMessage('Exception was added');
    },
  },

  created() {
    this.getTags();
  },
};
</script>
